import { supabase } from '@/lib/supabase';

const CHAVE_CARTEIRA = 'carteira:';

/** Uma carteira de fichas, já pronta para o cartão da lista. */
export interface CarteiraLinha {
  codigo: string;
  eventoId: number;
  eventoNome: string;
  eventoInicio: string | null;
  capa: string | null;
  /** Fichas ainda não retiradas (nem presas em vale). */
  paraRetirar: number;
  ingressos: number;
}

/** Linha de `minhas_carteiras` (carteiras guardadas na conta). */
export interface CarteiraDaConta {
  carteira_codigo: string;
  evento_id: number;
  evento_nome: string;
  evento_inicio: string | null;
  evento_capa: string | null;
  ingressos: number;
  itens_loja: number;
  pendentes: number;
}

// Carteiras guardadas só neste aparelho (chaves "carteira:<evento>").
export async function carteirasDoAparelho(ignorar: Set<number>): Promise<CarteiraLinha[]> {
  const pares: { codigo: string; eventoId: number }[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const chave = localStorage.key(i);
      if (!chave || !chave.startsWith(CHAVE_CARTEIRA)) continue;
      const eventoId = Number(chave.slice(CHAVE_CARTEIRA.length));
      const codigo = localStorage.getItem(chave);
      if (!Number.isFinite(eventoId) || !codigo || ignorar.has(eventoId)) continue;
      pares.push({ codigo, eventoId });
    }
  } catch {
    return [];
  }

  const resultados = await Promise.all(
    pares.map(async (p): Promise<CarteiraLinha | null> => {
      try {
        const { data } = await supabase.rpc('loja_carteira_publica', { p_carteira: p.codigo });
        const d = data as {
          carteira?: {
            evento_nome?: string;
            evento_inicio?: string | null;
            evento_capa?: string | null;
          };
          itens?: { disponivel?: number }[];
          ingressos?: unknown[];
        } | null;
        if (!d?.carteira) return null;
        const paraRetirar = (d.itens ?? []).reduce((soma, i) => soma + (i.disponivel ?? 0), 0);
        return {
          codigo: p.codigo,
          eventoId: p.eventoId,
          eventoNome: d.carteira.evento_nome ?? 'Evento',
          eventoInicio: d.carteira.evento_inicio ?? null,
          capa: d.carteira.evento_capa ?? null,
          paraRetirar,
          ingressos: (d.ingressos ?? []).length,
        };
      } catch {
        return null;
      }
    }),
  );

  return resultados.filter((c): c is CarteiraLinha => c != null);
}

// Junta conta + aparelho sem repetir evento e ordena: próximos primeiro.
export function montarCarteiras(
  daConta: CarteiraDaConta[],
  doAparelho: CarteiraLinha[],
  agora = Date.now(),
): { proximos: CarteiraLinha[]; anteriores: CarteiraLinha[] } {
  const mapa = new Map<number, CarteiraLinha>();

  daConta.forEach((c) => {
    mapa.set(c.evento_id, {
      codigo: c.carteira_codigo,
      eventoId: c.evento_id,
      eventoNome: c.evento_nome,
      eventoInicio: c.evento_inicio,
      capa: c.evento_capa ?? null,
      paraRetirar: c.itens_loja ?? 0,
      ingressos: c.ingressos ?? 0,
    });
  });

  doAparelho.forEach((c) => {
    if (!mapa.has(c.eventoId)) mapa.set(c.eventoId, c);
  });

  const quando = (c: CarteiraLinha) => (c.eventoInicio ? new Date(c.eventoInicio).getTime() : null);
  const todos = [...mapa.values()];

  const proximos = todos
    .filter((c) => {
      const q = quando(c);
      return q == null || q >= agora;
    })
    .sort((a, b) => (quando(a) ?? Number.MAX_SAFE_INTEGER) - (quando(b) ?? Number.MAX_SAFE_INTEGER));

  const anteriores = todos
    .filter((c) => {
      const q = quando(c);
      return q != null && q < agora;
    })
    .sort((a, b) => (quando(b) ?? 0) - (quando(a) ?? 0));

  return { proximos, anteriores };
}

// Texto do cartão: "3 fichas · 1 ingresso" ou "2 fichas para retirar".
export function resumoCarteira(c: CarteiraLinha): string {
  const partes: string[] = [];
  if (c.paraRetirar > 0) {
    partes.push(`${c.paraRetirar} ${c.paraRetirar === 1 ? 'ficha' : 'fichas'}`);
  }
  if (c.ingressos > 0) {
    partes.push(`${c.ingressos} ${c.ingressos === 1 ? 'ingresso' : 'ingressos'}`);
  }
  if (partes.length === 0) return 'Fichas todas retiradas';
  if (c.ingressos === 0) return `${partes[0]} para retirar`;
  return partes.join(' · ');
}

export function dataCurta(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const hora = String(d.getHours()).padStart(2, '0');
  return `${dia}/${mes} às ${hora}h`;
}